import React, { useState, useEffect, useCallback } from 'react';
import { FaSync, FaCheck, FaExclamationTriangle, FaCloud } from 'react-icons/fa';
import syncEngine from '../../offline/SyncEngine';
import onlineDetector from '../../offline/OnlineDetector';
import offlineStorage from '../../offline/OfflineStorage';
import './SyncButton.scss';

const SyncButton = ({ onClick, showLabel = true, className = '' }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(onlineDetector.isOnline);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const summary = await offlineStorage.getQueueSummary();
      setPendingCount(summary.pending + summary.failed);
    } catch {}
    setIsOnline(onlineDetector.isOnline);
  }, []);

  useEffect(() => {
    refreshStatus();
    const intervalId = setInterval(refreshStatus, 5000);

    const unsubOnline = onlineDetector.onChange((online) => {
      setIsOnline(online);
    });

    const unsubSync = syncEngine.onSync((event) => {
      if (event.type === 'start') {
        setIsSyncing(true);
      } else if (event.type === 'complete') {
        setIsSyncing(false);
        setLastSyncTime(Date.now());
        refreshStatus();
      }
    });

    return () => {
      clearInterval(intervalId);
      unsubOnline();
      unsubSync();
    };
  }, [refreshStatus]);

  const handleClick = async () => {
    if (isSyncing) return;
    if (!isOnline) {
      if (onClick) onClick();
      return;
    }
    setIsSyncing(true);
    try {
      await syncEngine.syncNow();
      setLastSyncTime(Date.now());
      await refreshStatus();
    } catch {} finally {
      setIsSyncing(false);
    }
    if (onClick) onClick();
  };

  const getStatusIcon = () => {
    if (isSyncing) return <FaSync className="sync-button__icon sync-button__icon--spinning" />;
    if (!isOnline) return <FaCloud className="sync-button__icon sync-button__icon--offline" />;
    if (pendingCount > 0) return <FaSync className="sync-button__icon sync-button__icon--pending" />;
    return <FaCheck className="sync-button__icon sync-button__icon--synced" />;
  };

  const getStatusClass = () => {
    if (isSyncing) return 'sync-button--syncing';
    if (!isOnline) return 'sync-button--offline';
    if (pendingCount > 0) return 'sync-button--pending';
    return 'sync-button--synced';
  };

  return (
    <button
      className={`sync-button ${getStatusClass()} ${className}`.trim()}
      onClick={handleClick}
      disabled={isSyncing}
      title={
        !isOnline
          ? 'Offline - sync unavailable'
          : isSyncing
            ? 'Syncing...'
            : pendingCount > 0
              ? `${pendingCount} item${pendingCount === 1 ? '' : 's'} pending sync`
              : 'All synced'
      }
    >
      <span className="sync-button__icon-wrapper">
        {getStatusIcon()}
        {pendingCount > 0 && !isSyncing && (
          <span className="sync-button__badge">{pendingCount > 99 ? '99+' : pendingCount}</span>
        )}
      </span>
      {showLabel && (
        <span className="sync-button__label">
          {isSyncing ? 'Syncing...' : !isOnline ? 'Offline' : pendingCount > 0 ? 'Sync' : 'Synced'}
        </span>
      )}
    </button>
  );
};

export default SyncButton;
